import { Body, colors, Margin, Spacer } from '@spritz-finance/ui'
import styled from 'styled-components'
import { SubscriptionErrorType } from '../../../../../../graphql/generated/graphql'
import { cssVar } from '../../../../../../theme/utils'
import { Subscription } from '../../Bills'

const ErrorContainer = styled.div`
    background-color: var(--text-primary, ${colors.coral[50]});
    border-radius: 10px;
    padding: ${Margin.Small};
    display: flex;
    flex-direction: column;
`

const InlineButton = styled(Body.Med)`
    text-decoration: underline;
    &:hover {
        cursor: pointer;
    }
`

const useErrorTitle = (error: Subscription['error'], generic = true) => {
    if (generic) return 'Recurring payment failed'

    if (error?.type === SubscriptionErrorType.Allowance)
        return 'Recurring payment failed - Not enough allowance'
    if (error?.type === SubscriptionErrorType.Balance)
        return 'Recurring payment failed - Insufficient balance'

    return 'Recurring payment failed'
}

const useErrorSubtitle = (
    error: Subscription['error'],
    generic = true,
    onClickManage?: () => void
) => {
    if (generic)
        return (
            <>
                Go to Manage recurring payments to{' '}
                <InlineButton size={14} onClick={() => onClickManage?.()}>
                    learn more
                </InlineButton>
                .
            </>
        )

    if (error?.type === SubscriptionErrorType.Allowance)
        return 'The payment amount exceeds the token allowance set in your wallet. Increase the allowance below. We will attempt to make the payment for 3 more days.'
    if (error?.type === SubscriptionErrorType.Balance)
        return 'The payment amount exceeds the funds in your wallet. Increase the funds in your wallet to cover the payment amount for this month. Once you add the funds, retry your payment below. Or, we will attempt to make the payment for 3 more days.'

    return (
        <>
            Go to Manage recurring payments to{' '}
            <InlineButton size={14} onClick={() => onClickManage?.()}>
                learn more
            </InlineButton>
            .
        </>
    )
}

export const SubscriptionError: React.FC<{
    subscription: Subscription
    generic?: boolean
    onClickManage?: () => void
}> = ({ subscription, generic = false, onClickManage, children }) => {
    const title = useErrorTitle(subscription.error, generic)
    const subTitle = useErrorSubtitle(subscription.error, generic, onClickManage)

    if (!subscription.error) return null

    return (
        <>
            <ErrorContainer>
                <Body.Bold size={14} color={cssVar('--text-error', colors.coral[75])}>
                    {title}
                </Body.Bold>
                <Spacer height={8} />
                <Body.Med size={14} color={cssVar('--text-error', colors.coral[75])}>
                    {subTitle}
                </Body.Med>
                {children && (
                    <>
                        <Spacer height={16} />
                        {children}
                    </>
                )}
            </ErrorContainer>
            <Spacer height={32} />
        </>
    )
}
